import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Box } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { iconMap } from './iconMap';

const contentWidths = {
  1: 'w-56',
  2: 'w-[32rem]',
  3: 'w-[48rem]',
};

const columnClasses = {
  1: 'columns-1',
  2: 'columns-2',
  3: 'columns-3',
};

const normalizeSections = (sections) => {
  if (Array.isArray(sections) && sections.length > 0 && !('items' in sections[0])) {
    return [{ header: null, items: sections }];
  }

  if (Array.isArray(sections)) {
    return sections.map(section => ({
      ...section,
      header: section.header ?? section.section ?? null,
    }));
  }

  return [];
};

export const getVisibleSections = (sections, canRead) => normalizeSections(sections)
  .map(section => ({
    ...section,
    items: section.items.filter(item => !item.hidden && canRead(item.pageKey)
      && (item.requiredPageKeys || []).every(canRead)),
  }))
  .filter(section => section.items.length > 0);

export const MegaMenu = ({
  label,
  Icon,
  sections,
  canRead,
  isActive,
  columns = 1,
  testId,
}) => {
  const location = useLocation();
  const visibleSections = getVisibleSections(sections, canRead);

  if (visibleSections.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={`nav-item ${isActive(location.pathname) ? 'active' : ''}`}
          data-testid={testId}
        >
          <Icon className="h-4 w-4" strokeWidth={1.5} />
          {label}
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={`${contentWidths[columns]} max-w-[calc(100vw-2rem)] bg-slate-800 border-slate-700/50 p-3`}
        align="start"
        collisionPadding={16}
      >
        <div className={`${columnClasses[columns]} gap-5`}>
          {visibleSections.map((section, sectionIndex) => (
            <section key={`${section.header || 'items'}-${sectionIndex}`} className="mb-4 w-full break-inside-avoid">
              {section.header && (
                <h3 className="mb-1.5 px-2 text-[10px] font-semibold tracking-wider text-slate-500">
                  {section.header}
                </h3>
              )}
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const ItemIcon = iconMap[item.name] || Box;
                  return (
                    <div key={item.path}>
                      {item.subHeader && (
                        <p className="px-2 pt-1.5 text-[9px] font-semibold uppercase tracking-wider text-slate-600">
                          {item.subHeader}
                        </p>
                      )}
                      <DropdownMenuItem asChild>
                        <Link
                          to={item.path}
                          className="flex items-center gap-2 px-2 py-1.5 text-sm text-slate-300 hover:bg-white/5 hover:text-white"
                          data-testid={`nav-item-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <ItemIcon className="h-3.5 w-3.5 shrink-0 text-primary/70" strokeWidth={1.5} />
                          <span className="min-w-0 truncate">{item.name}</span>
                        </Link>
                      </DropdownMenuItem>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
